// The value rules for the code tables — conversation_codes, clip_codes, and
// respondent_codes. Each stores its value as text, and the codetype it points at
// declares what that text is meant to hold. These two functions are the only
// place that relationship is interpreted.
//
// Writes are strict and reads are lenient, deliberately. Strict writes stop new
// values from disagreeing with their codetype. Lenient reads keep values that
// predate the codetype's current declaration visible, rather than hiding them
// behind a rule they were never written under — a codetype's datatype can be
// changed at any time, and its existing values are not wrong for having been
// stored first.

import { CodeDataType, CodeOption, QueryRequest } from '@/types/pulse';
import { normalizeOptions } from '@/utils/query/codify/options';

/**
 * The labels a codetype constrains its values to, or an empty list when it
 * constrains nothing. Only interview codetypes carry these, on the
 * `query_request` their AI population runs from.
 */
export function codetypeOptions(codetype: {
  query_request?: unknown;
}): CodeOption[] {
  const stored = codetype.query_request as Partial<QueryRequest> | null;
  return normalizeOptions(stored?.options);
}

/**
 * Why a value was refused. Callers map this to wording that suits their
 * audience — a form field and an API response describe the same refusal
 * differently.
 */
export type CodeValueFailure =
  | 'empty'
  | 'boolean'
  | 'integer'
  | 'number'
  | 'option';

export type CodeValueCheck =
  | { ok: true; value: string }
  | { ok: false; reason: CodeValueFailure; error: string };

const refuse = (reason: CodeValueFailure, error: string): CodeValueCheck => ({
  ok: false,
  reason,
  error
});

/**
 * Checks a value against its codetype's datatype and returns the exact string to
 * store. Booleans normalize to lowercase 'true'/'false'; `NUMBER` takes whole
 * numbers only and `FLOAT` any finite one; text constrained by `options` must
 * match a label exactly, and is stored as that label.
 *
 * `error` is general-purpose wording for callers that have no voice of their
 * own. Anything user-facing should switch on `reason` instead.
 */
export function validateCodeValue(
  datatype: number | null,
  raw: string,
  options?: CodeOption[]
): CodeValueCheck {
  const value = raw.trim();
  if (!value) return refuse('empty', 'Value must not be empty');

  switch (datatype ?? CodeDataType.TEXT) {
    case CodeDataType.BOOLEAN: {
      const lower = value.toLowerCase();
      if (lower !== 'true' && lower !== 'false') {
        return refuse(
          'boolean',
          `Value must be 'true' or 'false' (got '${raw}')`
        );
      }
      return { ok: true, value: lower };
    }
    case CodeDataType.NUMBER: {
      if (!/^-?\d+$/.test(value)) {
        return refuse('integer', `Value must be a whole number (got '${raw}')`);
      }
      return { ok: true, value };
    }
    case CodeDataType.FLOAT: {
      if (!Number.isFinite(Number(value))) {
        return refuse('number', `Value must be a number (got '${raw}')`);
      }
      return { ok: true, value };
    }
    default: {
      const match = options?.find((option) => option.label === value);
      if (options?.length && !match) {
        return refuse(
          'option',
          `Value '${raw}' is not one of: ${options
            .map((option) => option.label)
            .join(', ')}`
        );
      }
      return { ok: true, value: match?.label ?? value };
    }
  }
}

/**
 * Keeps the codes worth showing and leaves out the ones whose value is not what
 * their codetype describes, warning once for the set.
 *
 * A value that disagrees with its codetype reads as noise where it lands — an
 * age of ' English"' in a column of numbers — so display surfaces drop it rather
 * than render it. Writes are checked, so anything dropped here was stored before
 * its codetype said what it says now.
 *
 * An empty value is kept: nothing was recorded, which is a thing surfaces show
 * in their own way rather than a disagreement. Codes whose codetype is absent
 * from `codetypes` are kept too, since a caller showing a subset is making no
 * claim about the rest.
 */
export function displayableCodes<
  T extends { value: string | null; codetype_id: number }
>(
  codes: T[],
  codetypes: { id: number; datatype: number | null }[],
  context: string
): T[] {
  const datatypes = new Map(codetypes.map((c) => [c.id, c.datatype]));
  const kept: T[] = [];
  let hidden = 0;

  for (const code of codes) {
    if (!code.value?.trim() || !datatypes.has(code.codetype_id)) {
      kept.push(code);
      continue;
    }
    if (matchesCodetype(datatypes.get(code.codetype_id) ?? null, code.value)) {
      kept.push(code);
    } else {
      hidden += 1;
    }
  }

  if (hidden > 0) {
    console.warn(
      `[codes] ${context}: hid ${hidden} value(s) that do not match the type their codetype declares`
    );
  }
  return kept;
}

/**
 * Reads a stored value back as the kind of thing its codetype describes, or null
 * when the two disagree.
 *
 * Looser than `validateCodeValue` by design: it asks what kind of value this is,
 * not how precisely it was written. A decimal under a `NUMBER` codetype reads as
 * a number, and `options` are not enforced at all, so editing a codetype's
 * options never blanks the values already recorded against it. Only a real
 * mismatch — a word where a number belongs — returns null, which display
 * surfaces treat as a row to warn about and leave out.
 *
 * Callers asking only whether a value fits should use `matchesCodetype` rather
 * than testing this result, which is the value itself and can legitimately be
 * falsy.
 */
export function parseCodeValue(
  datatype: number | null,
  value: string | null
): string | number | boolean | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  switch (datatype ?? CodeDataType.TEXT) {
    case CodeDataType.BOOLEAN: {
      const lower = trimmed.toLowerCase();
      if (lower === 'true' || lower === '1') return true;
      if (lower === 'false' || lower === '0') return false;
      return null;
    }
    case CodeDataType.NUMBER:
    case CodeDataType.FLOAT: {
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    }
    default:
      return trimmed;
  }
}

/**
 * Whether a value reads back as the kind its codetype describes.
 *
 * The question `parseCodeValue` is usually asked, answered as a boolean so no
 * caller has to remember that its result is the value itself — a stored `false`
 * and a stored `0` both fit their codetype and are both falsy, so a truthiness
 * test would report them as failures.
 *
 * A value that is empty or absent matches nothing and reports false. Display
 * surfaces should filter with `displayableCodes` rather than this, since it
 * keeps blanks: having recorded nothing is not the same as having recorded
 * something that disagrees.
 */
export function matchesCodetype(
  datatype: number | null,
  value: string | null
): boolean {
  return parseCodeValue(datatype, value) !== null;
}
